import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { OverlayPanel } from "primereact/overlaypanel";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import CourseService from "../../../services/CourseService";
import AlertService from "../../../services/AlertService";
import AuthContext from "../../../auth/AuthContext";
import { Skeleton } from "primereact/skeleton";
import ConfirmDialogBox from "../../confirmDialog/ConfirmDialogBox";
import { InputText } from "primereact/inputtext";

const RecentCoursesTable = ({ onEditCourse, reloadTrigger }) => {
  const [selectedClients, setSelectedClients] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const menuRefs = useRef({});
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const authContext = useContext(AuthContext);
  const [globalFilter, setGlobalFilter] = useState("");
  const [allCourses, setAllCourses] = useState([]);

  useEffect(() => {
    const courseAccess = authContext.getPermissions("courses");
    if (
      courseAccess.filter((f) => f.access.includes(1)).length === 0 ||
      authContext.onGoingValidations
    ) {
      navigate("/");
    }
    setShowAdd(courseAccess.filter((f) => f.access.includes(3)).length > 0);
    setShowEdit(courseAccess.filter((f) => f.access.includes(3)).length > 0);
    setShowDelete(courseAccess.filter((f) => f.access.includes(4)).length > 0);
  }, [navigate, authContext]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteCourseId, setDeleteCourseId] = useState(null);
  const [deleteCourseName, setDeleteCourseName] = useState('');

  const createSkeletonData = () => {
    return Array.from({ length: rowsPerPage }).map((_, index) => ({
      id: `skeleton-${index}`,
      sno: index + 1,
      course: '',
      createDate: '',
      createdByUser: '',
      modifiedByUser: '',
      description: ''
    }));
  };

const loadCourses = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await CourseService.GetAll(authContext, signal);

      if (signal?.aborted) return;

      if (data.isSuccess) {
       // Sort courses by createDate in descending order
        const sortedCourses = data.data
          .map((course, index) => ({
            id: course.id,
            // sno: index + 1,
            course: course.courseName,
            createDate: course.createdDate
              ? new Date(course.createdDate).toLocaleDateString('en-GB')
              : '-',
            createdByUser: course.createdByUser,
            modifiedByUser: course.modifiedByUser,
            description: course.description,
            rawCreateDate: course.createdDate
          }))
          .sort((a, b) => new Date(b.rawCreateDate) - new Date(a.rawCreateDate))
          .map((course, index) => ({
            ...course,
            sno: index + 1
          }));

        setCourses(sortedCourses);
        setAllCourses(sortedCourses);
        const totalPages = Math.ceil(sortedCourses.length / rowsPerPage);
        if (currentPage >= totalPages && totalPages > 0) {
          setCurrentPage(totalPages - 1);
        }
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, currentPage, rowsPerPage]);

useEffect(() => {
    const controller = new AbortController(); 
    loadCourses(controller.signal);
    return () => controller.abort();
  }, [loadCourses, reloadTrigger]);

// Generate slug from course title
  const generateSlug = (title) => {
    return title.toLowerCase().replace(/\s+/g, "-");
  };

  const onGlobalFilterChange = (e) => {
  const value = e.target.value.toLowerCase();
  setGlobalFilter(value);

  if (value.trim() === "") {
    setCourses(allCourses);
  } else {
    const filtered = allCourses.filter((c) =>
      c.course.toLowerCase().includes(value)
    );
    setCourses(filtered);
  }
  setCurrentPage(0);
};

  const paginatedClients = courses.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const paginatedClientsToRender = loading ? createSkeletonData() : paginatedClients;

  const handleDeleteConfirmation = (id, name) => {
    setDeleteCourseId(id);
    setDeleteCourseName(name);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (deleteCourseId) {
      setLoading(true);
      const data = await CourseService.Remove(deleteCourseId, authContext);
      setShowDeleteDialog(false);
      if (data.isSuccess) {
        await loadCourses();
        const totalCoursesAfterDelete = courses.length - 1;
        const totalPages = Math.ceil(totalCoursesAfterDelete / rowsPerPage);
        if (currentPage >= totalPages && totalPages > 0) {
          setCurrentPage(totalPages - 1);
        }
        AlertService.success("Course deleted successfully!");
      } else {
        AlertService.error(data.errorMessage);
      }
      setLoading(false);
      setDeleteCourseId(null);
      setDeleteCourseName('');
    }
  };

  const actionTemplate = (rowData) => {
    if (loading) return <Skeleton width="100%" height="1.5rem" />;

    if (!menuRefs.current[rowData.id]) {
      menuRefs.current[rowData.id] = React.createRef();
    }
    const ref = menuRefs.current[rowData.id];

const handleViewCourse = (id, courseName) => {
  const slug = generateSlug(courseName);
  sessionStorage.setItem("selectedCourseId", id);
  navigate(`/courses/${slug}`, { state: { id } });
};


    return (
      <>
        <Button
          icon="pi pi-ellipsis-v"
          className="p-button-text pt-0 pb-0"
          onClick={(e) => ref.current.toggle(e)}
        />
        <OverlayPanel ref={ref} style={{ minWidth: "160px", padding: 0 }}>
          <div className="list-group">
            <button
              className="list-group-item list-group-item-action d-flex align-items-center gap-2 text-dark"
              onClick={() => handleViewCourse(rowData.id, rowData.course)}
            >
              <i className="pi pi-eye text-secondary"></i> View
            </button>
            {showEdit && (
              <button
                className="list-group-item list-group-item-action d-flex align-items-center gap-2 text-success"
                onClick={() => {
                  ref.current.hide();
                  onEditCourse(rowData.id);
                }}
              >
                <i className="pi pi-pencil"></i> Edit
              </button>
            )}
            {showDelete && (
              <button
                className="list-group-item list-group-item-action d-flex align-items-center gap-2 text-danger"
                onClick={() => {
                  ref.current.hide();
                  handleDeleteConfirmation(rowData.id, rowData.course);
                }}
              >
                <i className="pi pi-trash"></i> Delete
              </button>
            )}
          </div>
        </OverlayPanel>
      </>
    );
  };

  const courseTemplate = (rowData) => {
    if (loading) return <Skeleton width="100%" height="1.5rem" />;

    const slug = generateSlug(rowData.course);
    return (
     <span
  className="text-primary"
  style={{ cursor: "pointer", textDecoration: "none" }}
  onClick={() => {
    sessionStorage.setItem("selectedCourseId", rowData.id); 
    navigate(`/courses/${slug}`, { state: { id: rowData.id } });
  }}
>
  {rowData.course}
</span>

    );
  };

  const generatePageNumbers = () => {
    const totalPages = Math.ceil(courses.length / rowsPerPage);
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPageNumber > maxVisiblePages + 1) pages.push("...");
      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);
      if (currentPageNumber <= maxVisiblePages) end = 4;
      else if (currentPageNumber > totalPages - maxVisiblePages) start = totalPages - 3;
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
      if (totalPages > 1) pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="mt-3">
<div className="d-flex justify-content-between flex-wrap align-items-center mb-3">
  <h5 className="mb-0">
    {loading ? "Recent Courses" : courses.length === 1 ? "Recent Course" : "Recent Courses"}
  </h5>
          {showAdd && (
  <div className="global-search-wrapper">
    <i className="pi pi-search search-icon-left"></i>

    <InputText
      value={globalFilter}
      onChange={onGlobalFilterChange}
      placeholder="Search Course"
      className="global-search-input text-primary"
    />

    {globalFilter && (
      <i
        className="pi pi-times search-icon-right"
        onClick={() => {
          setGlobalFilter("");
          setCourses(allCourses);
          setCurrentPage(0);
        }}
      ></i>
    )}
  </div>
          )}
</div>
      <DataTable
        value={paginatedClientsToRender}
        selection={selectedClients}
        onSelectionChange={(e) => setSelectedClients(e.value)}
        dataKey="id"
        className="custom-table"
        responsiveLayout="scroll"
        width="100%"
      >
<Column
  header="S. No."
  style={{ width: "70px" }}
  body={
    loading
      ? () => <Skeleton width="60px" height="1.5rem" />
      : (_rowData, { rowIndex }) => currentPage * rowsPerPage + rowIndex + 1
  }
/>
        <Column
          field="course"
          header="Course Name"
          body={courseTemplate}
          style={{ width: "300px" }}
        />
        <Column
          field="createDate"
          header="Created Date"
          body={(rowData) =>
            loading ? <Skeleton width="60px" height="1.5rem" /> : rowData.createDate
          }
          style={{ width: "180px" }}
        />
        {/* <Column
          field="createdByUser"
          header="Created By"
          body={(rowData) =>
            loading ? <Skeleton width="60px" height="1.5rem" /> : rowData.createdByUser
          }
          style={{ width: "160px" }}
        />
        <Column
          field="modifiedByUser"
          header="Modified By"
          body={(rowData) =>
            loading ? <Skeleton width="60px" height="1.5rem" /> : rowData.modifiedByUser
          }
          style={{ width: "160px" }}
        /> */}
        <Column
          header="Action"
          body={actionTemplate}
          style={{ width: "70px" }}
        />
      </DataTable>
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-2 gap-2">
        <div className="d-flex align-items-center">
          <label className="me-2">Show Rows:</label>
          <select
            className="form-select form-select-sm w-auto text-secondary custom-input-color"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setCurrentPage(0);
            }}
          >
            {[5, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex justify-content-center align-items-center gap-2">
          <button
            className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
            style={{ width: "40px", height: "40px" }}
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
          >
            &lt;
          </button>
          {generatePageNumbers().map((page, index) =>
            page === "..." ? (
              <span key={index} className="mx-1 text-secondary">...</span>
            ) : (
              <button
                key={index}
                className={`btn p-0 d-flex align-items-center justify-content-center text-secondary pagination-item ${
                  currentPage + 1 === page ? "active-page" : ""
                }`}
                style={{ width: "40px", height: "40px" }}
                onClick={() => setCurrentPage(page - 1)}
              >
                {page}
              </button>
            )
          )}
          <button
            className="btn p-0 d-flex align-items-center justify-content-center fs-5 pagination-item"
            style={{ width: "40px", height: "40px" }}
            onClick={() =>
              setCurrentPage(
                Math.min(
                  Math.ceil(courses.length / rowsPerPage) - 1,
                  currentPage + 1
                )
              )
            }
            disabled={currentPage >= Math.ceil(courses.length / rowsPerPage) - 1}
          >
            &gt;
          </button>
        </div>
      </div>
      <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={async () => {
          setShowDeleteDialog(false);
          await handleDelete();
        }}
        title={
          <>
            Are you sure you want to delete the course{" "}
            <strong>{deleteCourseName}</strong>?
          </>
        }
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default RecentCoursesTable;